import type {
  GetQuestionEvaluationsResponse,
  QuestionEvaluationRow,
} from '@compass/client/types/analytics';
import { useQuery } from '@tanstack/react-query';

import type { SupportedTimeZone } from '@/constants/date';
import { analyticsService } from '@/services/api';
import { naiveDateToUtc } from '@/utils/date';
import { serializeMultiValueParam } from '@/utils/urlParams';

import { analyticsQueryKeys } from './useOrgAnalytics';

interface UseQuestionEvaluationsArgs {
  organizationId: string | null;
  metricKey: string | null;
  programIds?: string[];
  agentIds?: string[];
  callDirections?: string[];
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
  page?: number;
  pageSize?: number;
}

// buildQuestionEvaluationsExportParams builds the shared query params (program +
// timezone-correct dates) for the "export the full list" fetch, which pages
// through this same endpoint. Kept in sync with the query below so the export
// scope matches the on-screen preview exactly.
export const buildQuestionEvaluationsExportParams = (args: {
  programIds?: string[];
  agentIds?: string[];
  callDirections?: string[];
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
}): Record<string, string | undefined> => ({
  program_ids: args.programIds && args.programIds.length ? args.programIds.join(',') : undefined,
  agent_ids: args.agentIds && args.agentIds.length ? args.agentIds.join(',') : undefined,
  call_directions: serializeMultiValueParam(args.callDirections),
  start: args.start ? naiveDateToUtc(args.start, args.timeZone, 'start') : undefined,
  end: args.end ? naiveDateToUtc(args.end, args.timeZone, 'end') : undefined,
});

// EXPORT_PAGE_SIZE matches the API's maximum page_size (service.MaxQuestionEvaluationsPageSize).
// Asking for more is silently clamped, so the export pages at exactly the cap.
const EXPORT_PAGE_SIZE = 100;

interface FetchAllQuestionEvaluationsArgs {
  organizationId: string;
  metricKey: string;
  // Shared scope params from buildQuestionEvaluationsExportParams.
  params: Record<string, string | undefined>;
  // Hard stop so a pathological question can't download an unbounded payload.
  maxRows: number;
  // Applied per request — the missed-evaluation query can run tens of seconds.
  timeoutMs?: number;
}

// fetchAllQuestionEvaluations collects the FULL missed-evaluation list for the
// export by paging through the endpoint, because a single request is capped at
// EXPORT_PAGE_SIZE rows. Pages are fetched sequentially; the backend orders by
// (call_start_time DESC, evaluation_id DESC), so the tiebreak keeps paging
// stable and the concatenated result stays newest-first. Returns however many
// rows it reached — the caller compares against the expected total to tell the
// user when the maxRows cap truncated the file.
export const fetchAllQuestionEvaluations = async ({
  organizationId,
  metricKey,
  params,
  maxRows,
  timeoutMs,
}: FetchAllQuestionEvaluationsArgs): Promise<QuestionEvaluationRow[]> => {
  const collected: QuestionEvaluationRow[] = [];

  for (let page = 1; collected.length < maxRows; page += 1) {
    const response = await analyticsService.getQuestionEvaluations(
      { organization_id: organizationId, metric_key: metricKey },
      {
        params: { ...params, page, page_size: EXPORT_PAGE_SIZE },
        timeout: timeoutMs,
      },
    );

    const rows = response.evaluations ?? [];
    collected.push(...rows);

    // Stop on a short page (last one) or once the reported page count is spent.
    // Both guards are needed: a short page ends it early, and total_pages keeps
    // the loop bounded if the backend ever returns a full page at the end.
    const totalPages = response.pagination?.total_pages ?? page;
    if (rows.length < EXPORT_PAGE_SIZE || page >= totalPages) break;
  }

  return collected.slice(0, maxRows);
};

export const useQuestionEvaluations = (args: UseQuestionEvaluationsArgs) => {
  const { organizationId, metricKey, page = 1, pageSize = 20 } = args;

  const params: Record<string, string | number | undefined> = {
    program_ids: args.programIds && args.programIds.length ? args.programIds.join(',') : undefined,
    agent_ids: args.agentIds && args.agentIds.length ? args.agentIds.join(',') : undefined,
    call_directions: serializeMultiValueParam(args.callDirections),
    start: args.start ? naiveDateToUtc(args.start, args.timeZone, 'start') : undefined,
    end: args.end ? naiveDateToUtc(args.end, args.timeZone, 'end') : undefined,
    page,
    page_size: pageSize,
  };

  return useQuery({
    queryKey: [...analyticsQueryKeys.all, 'questions', 'evaluations', metricKey, params] as const,
    queryFn: (): Promise<GetQuestionEvaluationsResponse> =>
      analyticsService.getQuestionEvaluations(
        { organization_id: organizationId ?? '', metric_key: metricKey ?? '' },
        { params },
      ),
    enabled: !!organizationId && !!metricKey,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};
